import { Injectable, OnDestroy } from '@angular/core';
import {
  Router,
  Event as RouterEvent,
  NavigationStart,
  NavigationEnd,
  ActivationEnd,
  ActivatedRouteSnapshot, ActivatedRoute
} from "@angular/router";
import { Subject, Subscription } from "rxjs";
import { NgClass } from "@angular/common";


export interface NavigationRouteItem {
  /** The root relative URL of the route */
  url: string
  /** The title of the route */
  title: string
  /** Configuration data for the route */
  data: any
  /** Gets an indication, if the navigation item should be hidden in side navigation. */
  hideInSideNavigation: boolean
  /** Gets an indication, if the navigation item prevents the side navigation from appearing */
  disableSideNavigation: boolean
  /** Gets an indication, if the main application header should be hidden for this item */
  hideHeader: boolean
  /** Gets an indication, if the main application breadcrumbs should be hidden for this item */
  hideBreadcrumbs: boolean
  /** Gets a indication, if this is a root item of the navigation stack, e.g. has no parent route. */
  isRoot: boolean
  /** Gets the breadcrumb label text associated with this route. */
  breadCrumbLabel: string,
  /** Gets the relative position of the route compared to the last route in the router stack. */
  relativePathFromLastRoute: string
  /** Gets any additional CSS class definition associated with the route item */
  ngClass?: NgClass,
  /** Gets the collection of role claims, which are allowed for the route. The logged in user must be associated
   * with at least one role from this list. If this list os empty or undefined, all user roles are authorized to
   * access the route. */
  authorizedRoles?: string[]
}

@Injectable({
  providedIn: 'root'
})
export class NavigationStateService implements OnDestroy {

  private _routerSubscription?: Subscription;
  private _routeSnapshots: ActivatedRouteSnapshot[] = [];
  private _breadcrumbs: NavigationRouteItem[] = [];
  private readonly _breadcrumbsChange = new Subject<NavigationRouteItem[]>;
  private readonly _depthChange = new Subject<number>;

  get breadcrumbsChange(): Subject<NavigationRouteItem[]> {
    return this._breadcrumbsChange
  }

  get navigationDepth(): number {
    return this.breadcrumbs.length
  }

  get navigationDepthChange(): Subject<number> {
    return this._depthChange
  }

  get breadcrumbs(): NavigationRouteItem[] {
    return this._breadcrumbs
  }

  getNavigationDepth(route: ActivatedRoute) {
    let routeDepth = 0
    let snapshot: ActivatedRouteSnapshot | undefined | null = route.snapshot
    while (snapshot.parent) {
      snapshot = snapshot.parent
      routeDepth++
    }
    return routeDepth
  }

  constructor(
    private router: Router
  ) {
    this._routerSubscription = this.router.events.subscribe({
      next: (event: RouterEvent) => {
        if (event instanceof NavigationStart) {
          this._routeSnapshots.length = 0
        }
        if (event instanceof ActivationEnd) {
          this._routeSnapshots.splice(0, 0, event.snapshot)
        }
        if (event instanceof NavigationEnd){
          this._breadcrumbs = this.buildBreadcrumbs()
          this.breadcrumbsChange.next(this._breadcrumbs)
          this.navigationDepthChange.next(this._breadcrumbs.length)
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe()
  }

  unsubscribe(): void {
    this._routerSubscription?.unsubscribe()
    this._routerSubscription = undefined
  }

  buildBreadcrumbs(): NavigationRouteItem[] {
    return this._routeSnapshots.map(snapshot => this.mapToNavigationRoute(snapshot))
  }

  buildBreadcrumbsForRoute(snapshot: ActivatedRouteSnapshot): NavigationRouteItem[] {
    if (snapshot.parent) {
      return this.buildBreadcrumbsForRoute(snapshot.parent)
    }
    const results: NavigationRouteItem[] = []
    let firstChild = snapshot.firstChild
    while (firstChild) {
      results.push(this.mapToNavigationRoute(firstChild))
      firstChild = firstChild.firstChild
    }
    return results;
  }

  mapToNavigationRoute(snapshot: ActivatedRouteSnapshot): NavigationRouteItem {
    const resolvedPath = this.buildResolvedUrl(snapshot)
    const relativePathFromLatestRoute = this.buildRelativePathFromLatestRoute(snapshot)
    let title = snapshot.title ?? (snapshot.data['title'] ?? (resolvedPath ?? "ROOT")) as string
    if (snapshot.data['titleParam']) {
      const paramName = snapshot.data['titleParam']
      title = snapshot.params[paramName] ?? title
    }

    const snapshotData = snapshot.routeConfig && snapshot.routeConfig.data
      ? snapshot.routeConfig.data
      : snapshot.data

    return {
      url: resolvedPath,
      relativePathFromLastRoute: relativePathFromLatestRoute,
      title: this.populateParametersTemplate(title, snapshot),
      ngClass: snapshotData['ngClass'] ?? {},
      disableSideNavigation: snapshotData['disableSideNavigation'] ?? false,
      hideInSideNavigation: snapshotData['hideInSideNavigation'] ?? false,
      hideHeader: snapshotData['hideHeader'] ?? false,
      hideBreadcrumbs: snapshotData['hideBreadcrumbs'] || false,
      data: snapshot.data,
      isRoot: snapshot.parent != null,
      breadCrumbLabel: this.populateParametersTemplate(snapshotData['breadCrumbLabel'] ?? title, snapshot),
      authorizedRoles: snapshotData['authorizedRoles']
    }
  }

  buildResolvedUrl(snapshot: ActivatedRouteSnapshot): string {
    return snapshot.pathFromRoot
      .map(v => v.url
        .map(segment => decodeURI(segment.toString()))
        .join('/'))
      .join('/')
      .replace('//', '/')
  }

  buildRelativePathFromLatestRoute(snapshot: ActivatedRouteSnapshot): string {
    if (snapshot.children.length) {
      const childResult = this.buildRelativePathFromLatestRoute(snapshot.children[0])

      if (!childResult.length) {
        return '../'
      }
      else {
        return `${childResult}/../`.replace('//', '/')
      }
    }
    return ''
  }

  findParameterInRoutePath(snapshot: ActivatedRouteSnapshot, paramName: string): string | undefined {
    if (snapshot.firstChild) {
      return this.findParameterInRoutePath(snapshot.firstChild, paramName)
    }
    const v = snapshot.params[paramName]
    if (v) {
      return v
    }
    else if (snapshot.parent) {
      return this.findParameterInRoutePath(snapshot.parent, paramName)
    }
    return undefined
  }

  populateParametersTemplate(text: string, snapshot: ActivatedRouteSnapshot): string {
    const matches = Array.from(text.matchAll(/(?<={{)(.*?)(?=}})/g))
    let result = text
    matches.forEach(match => {
      const paramName = match[0]
      const paramValue = this.findParameterInRoutePath(snapshot, paramName)
      result = result.replace('{{' + paramName + '}}', paramValue || 'NULL')
    })
    return result
  }
}
